// Valorización del inventario a costo promedio por bodega.
// Inventario → Reportes → Valorización. Agrupada por bodega, expandible/contraíble.
import { hoyISO } from '@/lib/utils/regional';
import { Fragment, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, ChevronDown, ChevronRight, Loader2, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Location, fieldCls, locationsList, money, num, invRpc, invalidateInventario } from './shared';
import { ModalCorregirCosto } from './ModalCorregirCosto';

interface ValRow {
  location_id: string; location_nombre: string;
  producto_id: string; producto_codigo: string; producto_nombre: string;
  unidades: number; costo_promedio: number; valor: number;
}
interface Grupo { nombre: string; filas: ValRow[]; unidades: number; valor: number }

// Las filas "EN TRÁNSITO desde …" son saldo virtual atribuido al origen (ver
// inv_valorizacion): no corresponden 1:1 a inventario.stockcurrent de esa
// bodega, así que no se pueden corregir de costo desde aquí.
const esTransito = (nombre: string) => nombre.startsWith('EN TRÁNSITO');

export function Valorizacion() {
  const qc = useQueryClient();
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const [location, setLocation] = useState('');
  // FECHA DE CORTE: el inventario valorizado como estaba al 31 de diciembre, que
  // es lo que hay que cuadrar contra el mayor. A una fecha pasada se reconstruye
  // del kardex (sumando movimientos) y no incluye la mercadería en tránsito,
  // porque el kardex no sabe reconstruirla.
  const [corte, setCorte] = useState(() => hoyISO());
  const esHoy = corte >= hoyISO();
  const { data: rows = [], isFetching } = useQuery<ValRow[]>({
    queryKey: ['inv-valorizacion', location, corte],
    queryFn: () => invRpc<ValRow[]>('inv_valorizacion', { p_location: location || null, _corte: corte }),
  });
  const total = useMemo(() => rows.reduce((s, r) => s + Number(r.valor || 0), 0), [rows]);

  // Modal "Corregir costo": null = cerrado; {} = abierto vacío (botón de encabezado);
  // {locationId, productoId} = precargado desde una fila.
  const [corregir, setCorregir] = useState<{ locationId?: string; productoId?: string } | null>(null);

  // Agrupar por bodega (orden estable por nombre)
  const grupos = useMemo<Grupo[]>(() => {
    const m = new Map<string, Grupo>();
    for (const r of rows) {
      const k = r.location_nombre || '—';
      let g = m.get(k);
      if (!g) { g = { nombre: k, filas: [], unidades: 0, valor: 0 }; m.set(k, g); }
      g.filas.push(r); g.unidades += Number(r.unidades || 0); g.valor += Number(r.valor || 0);
    }
    return Array.from(m.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [rows]);

  const [colapsadas, setColapsadas] = useState<Set<string>>(new Set());
  const toggle = (n: string) => setColapsadas((s) => { const x = new Set(s); x.has(n) ? x.delete(n) : x.add(n); return x; });
  const contraerTodo = () => setColapsadas(new Set(grupos.map((g) => g.nombre)));
  const expandirTodo = () => setColapsadas(new Set());

  const exportFilas = useMemo<Celda[][]>(() => {
    const filas: Celda[][] = rows.map((r, i) => [i + 1, r.location_nombre, r.producto_codigo, r.producto_nombre,
      Number(r.unidades), Number(r.costo_promedio), Number(r.valor)]);
    if (filas.length) filas.push(['', 'Total', '', '', null, null, total]);
    return filas;
  }, [rows, total]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Calculator className="w-5 h-5" /> Valorización de inventario</h1>
        <Button variant="outline" size="sm" onClick={() => setCorregir({})}>
          <Wrench className="w-4 h-4" /> Corregir costo
        </Button>
      </div>
      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-4">
          <Campo label="Bodega">
            <select className={fieldCls} value={location} onChange={(e) => setLocation(e.target.value)}>
              <option value="">— Todas —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Valorizado al">
            <input type="date" className={fieldCls} value={corte}
              onChange={(e) => setCorte(e.target.value || hoyISO())} />
          </Campo>
          {!esHoy && (
            <p className="pb-2 text-xs text-amber-600 max-w-md">
              Reconstruido del kardex al <b>{corte}</b>. No incluye la mercadería en tránsito:
              el kardex no la sabe reconstruir a una fecha pasada.
            </p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Existencias valorizadas ({rows.length})</CardTitle>
          <div className="flex items-center gap-3">
            {grupos.length > 0 && (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={expandirTodo}>Expandir todo</Button>
                <Button variant="outline" size="sm" onClick={contraerTodo}>Contraer todo</Button>
              </div>
            )}
            <div className="text-sm">Total: <span className="font-semibold">{money(total)}</span></div>
            <ExportarBotones nombre="valorizacion_inventario" titulo="Valorización de inventario"
              headers={['N°', 'Bodega', 'Código', 'Producto', 'Unidades', 'Costo prom.', 'Valor']}
              filas={exportFilas} colsDerecha={[0, 4, 5, 6]} />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Bodega / Producto</th>
              <th className="py-2 pr-3 text-right">Unidades</th><th className="py-2 pr-3 text-right">Costo prom.</th><th className="py-2 pr-3 text-right">Valor</th>
              <th className="py-2 pr-3 text-center">Acción</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin existencias</td></tr>}
              {grupos.map((g) => {
                const abierta = !colapsadas.has(g.nombre);
                return (
                  <Fragment key={g.nombre}>
                    <tr className="border-b bg-muted/40 cursor-pointer hover:bg-muted/60" onClick={() => toggle(g.nombre)}>
                      <td className="py-2 pr-3 font-medium">
                        <span className="inline-flex items-center gap-1">
                          {abierta ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {g.nombre} <span className="text-xs text-muted-foreground">({g.filas.length})</span>
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right">{num(g.unidades, 2)}</td>
                      <td className="py-2 pr-3"></td>
                      <td className="py-2 pr-3 text-right font-semibold">{money(g.valor)}</td>
                      <td className="py-2 pr-3"></td>
                    </tr>
                    {abierta && g.filas.map((r, i) => {
                      const costoCero = Number(r.costo_promedio || 0) === 0;
                      const transito = esTransito(r.location_nombre);
                      return (
                        <tr key={`${g.nombre}-${i}`} className={`border-b last:border-0 hover:bg-muted/30 ${costoCero && !transito ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                          <td className="py-2 pr-3 pl-7"><span className="font-mono text-xs">{r.producto_codigo}</span> {r.producto_nombre}</td>
                          <td className="py-2 pr-3 text-right">{num(r.unidades, 2)}</td>
                          <td className={`py-2 pr-3 text-right ${costoCero && !transito ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{money(r.costo_promedio)}</td>
                          <td className="py-2 pr-3 text-right font-medium">{money(r.valor)}</td>
                          <td className="py-2 pr-3 text-center">
                            {!transito && (
                              <button type="button" title="Corregir costo"
                                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={() => setCorregir({ locationId: r.location_id, productoId: r.producto_id })}>
                                <Wrench className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
            {rows.length > 0 && <tfoot><tr className="border-t font-semibold"><td className="py-2 pr-3" colSpan={3}>Total</td><td className="py-2 pr-3 text-right">{money(total)}</td><td></td></tr></tfoot>}
          </table>
        </CardContent>
      </Card>

      {corregir && (
        <ModalCorregirCosto
          locationIdInicial={corregir.locationId}
          productoIdInicial={corregir.productoId}
          onClose={() => setCorregir(null)}
          onOk={() => { setCorregir(null); invalidateInventario(qc); }}
        />
      )}
    </div>
  );
}
